import { useState } from 'react'
import { Card, Button, Space, Input, message, Popconfirm, Empty, List, Typography } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { hospitalFaxNumbersApi, type FaxNumber } from '@/api/hospital-fax-numbers'
import { formatPhoneInput, validateUSPhone } from '@/utils/phone-formatter'
import { getApiErrorMessage } from '@/utils/error'

const { Text } = Typography

interface HospitalFaxNumbersProps {
  hospitalId: string
}

export const HospitalFaxNumbers = ({ hospitalId }: HospitalFaxNumbersProps) => {
  const [isAdding, setIsAdding] = useState(false)
  const [newFaxNumber, setNewFaxNumber] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['hospital-fax-numbers', hospitalId],
    queryFn: async () => {
      const response = await hospitalFaxNumbersApi.listFaxNumbers(hospitalId)
      return response.data.faxNumbers
    },
  })

  const createMutation = useMutation({
    mutationFn: async (faxNumber: string) => {
      return hospitalFaxNumbersApi.createFaxNumber(hospitalId, { faxNumber })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hospital-fax-numbers', hospitalId] })
      message.success('Fax number added successfully')
      setNewFaxNumber('')
      setIsAdding(false)
    },
    onError: (error: unknown) => {
      message.error(getApiErrorMessage(error, 'Failed to add fax number'))
    },
  })

  const updateMutation = useMutation({
    mutationFn: async ({ faxId, faxNumber }: { faxId: string; faxNumber: string }) => {
      return hospitalFaxNumbersApi.updateFaxNumber(hospitalId, faxId, { faxNumber })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hospital-fax-numbers', hospitalId] })
      message.success('Fax number updated successfully')
      setEditingId(null)
      setEditValue('')
    },
    onError: (error: unknown) => {
      message.error(getApiErrorMessage(error, 'Failed to update fax number'))
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (faxId: string) => {
      return hospitalFaxNumbersApi.deleteFaxNumber(hospitalId, faxId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hospital-fax-numbers', hospitalId] })
      message.success('Fax number deleted successfully')
    },
    onError: (error: unknown) => {
      message.error(getApiErrorMessage(error, 'Failed to delete fax number'))
    },
  })

  const handleAdd = () => {
    if (!newFaxNumber.trim()) {
      message.warning('Please enter a fax number')
      return
    }
    if (!validateUSPhone(newFaxNumber)) {
      message.error('Invalid phone number. Must be 10 digits.')
      return
    }
    createMutation.mutate(newFaxNumber)
  }

  const handleEdit = (faxNumber: FaxNumber) => {
    setEditingId(faxNumber.id)
    setEditValue(faxNumber.fax_number)
  }

  const handleSaveEdit = (faxId: string) => {
    if (!editValue.trim()) {
      message.warning('Please enter a fax number')
      return
    }
    if (!validateUSPhone(editValue)) {
      message.error('Invalid phone number. Must be 10 digits.')
      return
    }
    updateMutation.mutate({ faxId, faxNumber: editValue })
  }

  const handleCancelEdit = () => {
    setEditingId(null)
    setEditValue('')
  }

  const handleDelete = (faxId: string) => {
    deleteMutation.mutate(faxId)
  }

  const handleNewFaxNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneInput(e.target.value)
    setNewFaxNumber(formatted)
  }

  const handleEditValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneInput(e.target.value)
    setEditValue(formatted)
  }

  const faxNumbers = data || []

  return (
    <Card
      title="Fax Numbers"
      extra={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setIsAdding(!isAdding)}
          size="small"
        >
          {isAdding ? 'Cancel' : 'Add'}
        </Button>
      }
      loading={isLoading}
    >
      {isAdding && (
        <div style={{ marginBottom: 16 }}>
          <Space.Compact style={{ width: '100%' }}>
            <Input
              placeholder="Enter fax number (e.g., 555-123-4567)"
              value={newFaxNumber}
              onChange={handleNewFaxNumberChange}
              onPressEnter={handleAdd}
              maxLength={12}
              autoFocus
            />
            <Button type="primary" onClick={handleAdd} loading={createMutation.isPending}>
              Add
            </Button>
          </Space.Compact>
          <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
            Enter a US phone number (10 digits). Formatting will be applied automatically.
          </div>
        </div>
      )}

      {faxNumbers.length === 0 ? (
        <Empty description="No fax numbers configured for this hospital" />
      ) : (
        <List
          dataSource={faxNumbers}
          renderItem={(faxNumber: FaxNumber) => (
            <List.Item
              key={faxNumber.id}
              actions={
                editingId === faxNumber.id
                  ? [
                      <Button
                        key="save"
                        type="link"
                        icon={<CheckOutlined />}
                        onClick={() => handleSaveEdit(faxNumber.id)}
                        loading={updateMutation.isPending}
                        size="small"
                      >
                        Save
                      </Button>,
                      <Button
                        key="cancel"
                        type="link"
                        icon={<CloseOutlined />}
                        onClick={handleCancelEdit}
                        size="small"
                      >
                        Cancel
                      </Button>,
                    ]
                  : [
                      <Button
                        key="edit"
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(faxNumber)}
                        size="small"
                      >
                        Edit
                      </Button>,
                      <Popconfirm
                        key="delete"
                        title="Delete Fax Number"
                        description="Are you sure you want to delete this fax number?"
                        onConfirm={() => handleDelete(faxNumber.id)}
                        okText="Yes"
                        cancelText="No"
                      >
                        <Button
                          type="link"
                          danger
                          icon={<DeleteOutlined />}
                          loading={deleteMutation.isPending}
                          size="small"
                        >
                          Delete
                        </Button>
                      </Popconfirm>,
                    ]
              }
            >
              {editingId === faxNumber.id ? (
                <Input
                  value={editValue}
                  onChange={handleEditValueChange}
                  maxLength={12}
                  style={{ width: 200 }}
                  autoFocus
                />
              ) : (
                <Space direction="vertical" size={0}>
                  <Text strong style={{ fontSize: 14 }}>
                    {faxNumber.fax_number}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Type: {faxNumber.type}
                  </Text>
                </Space>
              )}
            </List.Item>
          )}
        />
      )}

      <div style={{ marginTop: 16, fontSize: 12, color: '#666' }}>
        Total fax numbers: {faxNumbers.length}
      </div>
    </Card>
  )
}
